import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AppBar, Menu, MenuItem, Badge, Typography, IconButton, Divider, Button, Avatar, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { ListItem, Box } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import profileImage from "./profile.png";
import CheckIcon from '@mui/icons-material/Check';
import Bell from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import { Chip, CircularProgress, Tooltip } from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DoneAllIcon from '@mui/icons-material/DoneAll';

const Navbar = () => {
  const [anchorElProfile, setAnchorElProfile] = useState(null);
  const [anchorElNotifications, setAnchorElNotifications] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
const [messages, setMessages] = useState([]);
const [hasMoreNotifications, setHasMoreNotifications] = useState(false);
const hasMoreRef = useRef(false);
const [notifPageNo, setNotifPageNo] = useState(1);
const notifPageNoRef = useRef(1);
const [loadingMoreNotifs, setLoadingMoreNotifs] = useState(false);
const intervalRef = useRef(null);
const notifListRef = useRef(null);
const [markingAll, setMarkingAll] = useState(false);
  const navigate = useNavigate();
  const teacherName = sessionStorage.getItem("clientName");
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const token = sessionStorage.getItem("token");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchUnreadCount();
    intervalRef.current = setInterval(fetchUnreadCount, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

 const fetchNotifications = async (pageNo = 1, append = false) => {
    try {
      const response = await fetch(
        `${apiUrl}/NewNotification/GetMyNotifications?PageNo=${pageNo}&PageSize=20`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        const items = Array.isArray(data.data) ? data.data : [];
        setMessages(prev => append ? [...prev, ...items] : items);
        setHasMoreNotifications(items.length === 20);
        hasMoreRef.current = items.length === 20;
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setMessages([]);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(
        `${apiUrl}/NewNotification/GetUnreadCount`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setUnreadMessages(typeof data.unreadCount === 'number' ? data.unreadCount : 0);
      }
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorElProfile(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorElProfile(null);
  };

  const handleProfileDetailsClick = () => {
    handleProfileMenuClose();
    navigate("/profile-details");
  };

  const handleLogout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("clientName");
    // Add other session items if needed
    handleProfileMenuClose();
    navigate("/"); // Or "/" as appropriate
  };

  const handleNotificationsClick = (event) => {
    setAnchorElNotifications(event.currentTarget);
    setNotifPageNo(1);
    notifPageNoRef.current = 1;
    fetchNotifications(1, false);
  };

  const handleCloseNotifications = () => {
    setAnchorElNotifications(null);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

 const markNotificationAsRead = async (recipientId) => {
    setMessages(prev =>
      prev.map(n => n.recipientId === recipientId ? { ...n, isRead: true } : n)
    );
    setUnreadMessages(prev => Math.max(0, prev - 1));
    try {
      const response = await fetch(`${apiUrl}/NewNotification/MarkAsRead`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ recipientId }),
      });
      if (!response.ok) throw new Error('Failed');
    } catch {
      setMessages(prev =>
        prev.map(n => n.recipientId === recipientId ? { ...n, isRead: false } : n)
      );
      setUnreadMessages(prev => prev + 1);
    }
  };

  const markAllAsRead = async () => {
    setMarkingAll(true);
    try {
      await fetch(`${apiUrl}/NewNotification/MarkAllAsRead`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
      });
      setMessages(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadMessages(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    } finally {
      setMarkingAll(false);
    }
  };

  // Sanitize names coming from API (remove surrounding brackets/quotes etc.)
  const sanitizeName = (name) => {
    if (name === undefined || name === null) return '';
    return String(name).replace(/^[\s\(\[\{"'`]+|[\s\)\]\}\"'`]+$/g, '').trim();
  };

  const sanitizedTeacherName = sanitizeName(teacherName);

  return (
    <>
    <AppBar
  position="fixed"
  elevation={0}
  sx={{
    background: `
  linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)
`,
boxShadow: '0 4px 18px rgba(245,158,11,0.35)',
    height: 55,
    top: 0,
    left: { xs: 0, md: 240 },
    width: { xs: '100%', md: `calc(100% - 240px)` },
    zIndex: 900, // Higher than typical drawer (1100)
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  }}
>
        <Toolbar
          sx={{
            minHeight: 55,
            height: 55,
            px: { xs: 1.5, sm: 3 },
            display: 'flex',
            justifyContent: { xs: 'space-between', sm: 'space-between' },
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleMobileMenuOpen}
                aria-label="open drawer"
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                fontWeight: 600,
                color: 'white',
                flexShrink: 0,
              }}
            >
              Teacher Dashboard
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            <Button
              variant="text"
              color="inherit"
              onClick={handleProfileMenuOpen}
              startIcon={
                <Avatar
                  src={profileImage}
                  sx={{ width: 24, height: 24 }}
                />
              }
              sx={{
                textTransform: 'none',
                px: { xs: 0.5, sm: 1 },
                py: 0.5,
                minWidth: 'auto',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: 1,
                },
                borderRadius: 1,
                display: { xs: 'none', sm: 'flex' }, // Hide on mobile, show on larger
              }}
            >
              <Typography
                noWrap
                sx={{
                  maxWidth: { xs: 100, sm: 150 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 500,
                }}
              >
                Welcome, {sanitizedTeacherName || "Teacher"}!
              </Typography>
            </Button>

            {/* Mobile Profile Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                onClick={handleProfileMenuOpen}
                sx={{
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: 1,
                  },
                }}
              >
                <Avatar src={profileImage} sx={{ width: 32, height: 32 }} />
              </IconButton>
            )}

            <IconButton
              color="inherit"
              onClick={handleNotificationsClick}
              sx={{
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: 1,
                },
              }}
            >
              <Badge
                badgeContent={unreadMessages > 0 ? unreadMessages : 0}
                color="error"
                overlap="circular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.75rem',
                    minWidth: 16,
                    height: 16,
                  },
                }}
              >
                <Bell sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </Badge>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer - You can integrate this with your sidebar logic */}
      {/* For now, placeholder for mobile menu */}
      {isMobile && mobileMenuOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 55, // Below navbar
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'background.paper',
            zIndex: 1100,
            transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.3s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Add your sidebar menu items here for mobile */}
          <IconButton onClick={handleMobileMenuClose} sx={{ alignSelf: 'flex-end', p: 2 }}>
            Close
          </IconButton>
          {/* Menu items: Dashboard, Time Table, etc. */}
        </Box>
      )}

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorElProfile}
        open={Boolean(anchorElProfile)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 150,
            borderRadius: 1,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <MenuItem
          onClick={handleProfileDetailsClick}
          sx={{ px: 2, py: 1 }}
        >
          <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
          Profile Details
        </MenuItem>
        <MenuItem
          onClick={handleLogout}
          sx={{ px: 2, py: 1 }}
        >
          <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
          Logout
        </MenuItem>
      </Menu>

    {/* Notifications Menu */}
<Menu
  anchorEl={anchorElNotifications}
  open={Boolean(anchorElNotifications)}
  onClose={handleCloseNotifications}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
  PaperProps={{
    sx: {
      width: { xs: '90vw', sm: 400 },
      maxHeight: 500,
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 2,
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      mt: 1,
      p: 0,
    },
  }}
>
  {/* Header */}
  <Box sx={{
    background: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%))',
    px: 2.5, py: 1.5,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Bell sx={{ color: '#fff', fontSize: 20 }} />
      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>
        Notifications
      </Typography>
      {unreadMessages > 0 && (
        <Chip
          label={unreadMessages}
          size="small"
          sx={{ bgcolor: '#f44336', color: '#fff', fontWeight: 700, height: 20, fontSize: '0.7rem', ml: 0.5 }}
        />
      )}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {unreadMessages > 0 && (
        <Tooltip title="Mark all as read">
          <IconButton
            size="small"
            onClick={markAllAsRead}
            disabled={markingAll}
            sx={{ color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#fff' } }}
          >
            {markingAll ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <DoneAllIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
      <IconButton size="small" onClick={handleCloseNotifications} sx={{ color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#fff' } }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  </Box>

  {/* Scrollable list */}
  <Box
    ref={notifListRef}
    sx={{
      flex: 1,
      overflowY: 'auto',
      maxHeight: 380,
      '&::-webkit-scrollbar': { width: 5 },
      '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
      '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: 3 },
    }}
    onScroll={async (e) => {
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 60 && hasMoreRef.current && !loadingMoreNotifs) {
        setLoadingMoreNotifs(true);
        const next = notifPageNoRef.current + 1;
        notifPageNoRef.current = next;
        setNotifPageNo(next);
        await fetchNotifications(next, true);
        setLoadingMoreNotifs(false);
      }
    }}
  >
    {messages.length === 0 ? (
      <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
        <Bell sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          You're all caught up!
        </Typography>
        <Typography variant="caption" color="text.disabled">
          No notifications yet.
        </Typography>
      </Box>
    ) : (
      messages.map((notif, index) => {
        const typeColors = { Reminder: '#ff9800', Success: '#4caf50', Alert: '#f44336', Info: '#2196f3' };
        const dotColor = typeColors[notif.notificationType] || '#9c27b0';
        const initials = (notif.title || sanitizeName(notif.name) || '').charAt(0).toUpperCase() || '🔔';
        return (
          <Box
            key={notif.recipientId}
            onClick={() => { if (!notif.isRead) markNotificationAsRead(notif.recipientId); }}
            sx={{
              display: 'flex', gap: 1.5, px: 2, py: 1.5,
              cursor: notif.isRead ? 'default' : 'pointer',
              bgcolor: !notif.isRead ? 'rgba(63,81,181,0.06)' : 'transparent',
              borderLeft: !notif.isRead ? '3px solid #e1c67b' : '3px solid transparent',
              borderBottom: index < messages.length - 1 ? '1px solid #f0f0f0' : 'none',
              transition: 'background 0.15s',
              '&:hover': { bgcolor: !notif.isRead ? 'rgba(63,81,181,0.12)' : 'rgba(0,0,0,0.04)' },
            }}
          >
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar sx={{
                width: 40, height: 40, fontWeight: 700, fontSize: '1rem',
                background: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%))',
              }}>
                {initials}
              </Avatar>
              <Box sx={{
                position: 'absolute', bottom: 0, right: 0,
                width: 11, height: 11, borderRadius: '50%',
                bgcolor: dotColor, border: '2px solid #fff',
              }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="subtitle2"
                  fontWeight={notif.isRead ? 500 : 700}
                  color={notif.isRead ? 'text.secondary' : 'text.primary'}
                  sx={{ lineHeight: 1.3, flex: 1, pr: 1 }}
                  noWrap
                >
                  {notif.title || sanitizeName(notif.name)}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap', fontSize: '0.68rem', mt: 0.2 }}>
                  {notif.createdOn
                    ? new Date(notif.createdOn).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                    : notif.timeAgo || 'Just now'}
                </Typography>
              </Box>

              <Tooltip title={notif.message || ''} placement="left" arrow
                componentsProps={{
                  tooltip: { sx: { bgcolor: '#0B1F3D', color: '#fff', fontSize: '0.78rem', maxWidth: 280, p: '10px 14px', borderRadius: 2 } },
                  arrow: { sx: { color: '#0B1F3D' } },
                }}
              >
                <Typography variant="body2" color="text.secondary"
                  sx={{ fontSize: '0.8rem', lineHeight: 1.4, mt: 0.3,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', cursor: 'default' }}
                >
                  {notif.message}
                </Typography>
              </Tooltip>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {notif.notificationType && (
                    <Chip label={notif.notificationType} size="small"
                      sx={{ height: 16, fontSize: '0.62rem', fontWeight: 600,
                        bgcolor: `${dotColor}18`, color: dotColor, px: 0.5 }} />
                  )}
                </Box>
                {!notif.isRead && (
                  <Tooltip title="Mark as read">
                    <IconButton size="small"
                      onClick={e => { e.stopPropagation(); markNotificationAsRead(notif.recipientId); }}
                      sx={{ color: '#3f51b5', p: 0.5, '&:hover': { bgcolor: 'rgba(63,81,181,0.1)' } }}
                    >
                      <MarkEmailReadIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        );
      })
    )}

    {loadingMoreNotifs && (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} sx={{ color: '#3f51b5' }} />
      </Box>
    )}
  </Box>

  {/* Footer */}
  {messages.length > 0 && (
    <Box sx={{ borderTop: '1px solid #f0f0f0', px: 2, py: 1, display: 'flex', justifyContent: 'center', bgcolor: 'grey.50' }}>
      <Typography variant="caption"
        sx={{ color: '#3f51b5', cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem', '&:hover': { textDecoration: 'underline' } }}
       onClick={() => { handleCloseNotifications(); navigate('/teacher-notifications'); }}
      >
        View all notifications →
      </Typography>
    </Box>
  )}
</Menu>
    </>
  );
};

export default Navbar;